import React, { useCallback, useEffect, useState } from 'react';
import { X, Pencil, Check, Loader2, Lock, GitBranch, History, Target } from 'lucide-react';
import { pmApi, type TaskDetail } from './pmApi';
import {
  RiskLevel, ReasonList, EmptyState, LoadingBlock, OriginBadge, fmtDate, fmtWhen, cleanName, cleanBlocker,
} from './components';
import { Button } from '../../../components/new-components/ui/button';
import { Badge } from '../../../components/new-components/ui/badge';
import { Input } from '../../../components/new-components/ui/input';
import { Separator } from '../../../components/new-components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/new-components/ui/select';

/**
 * The working surface for a single item.
 *
 * Selecting a row should answer "why does this look like this, and what do I
 * do about it" without another navigation — so risk reasoning, dependencies in
 * both directions, and the item's own change history all live here, alongside
 * the two fields leadership actually owns.
 */

const STATUSES = ['Not Started', 'Working on it', 'Stuck', 'Done'];

/** Inline text field that commits on Enter / blur. */
function InlineField({
  label, value, placeholder, onSave, emphasizeEmpty,
}: {
  label: string;
  value: string | null;
  placeholder: string;
  onSave: (v: string | null) => Promise<void>;
  emphasizeEmpty?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value || ''); }, [value]);

  const commit = async () => {
    if ((draft.trim() || null) === (value || null)) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(draft.trim() || null); setEditing(false); } finally { setSaving(false); }
  };

  return (
    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      {editing ? (
        <div className="flex items-center gap-1.5">
          <Input
            autoFocus
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
            }}
            className="h-8 text-sm"
          />
          <Button size="sm" className="h-8 w-8 shrink-0 p-0" onClick={commit} disabled={saving} aria-label={`Save ${label}`}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={`group flex w-full items-start gap-2 rounded-md border px-2.5 py-2 text-left text-sm transition-colors hover:bg-muted/60 ${
            value
              ? 'border-border'
              : emphasizeEmpty
                ? 'border-dashed border-orange-300 text-orange-700 dark:border-orange-800 dark:text-orange-400'
                : 'border-dashed border-border text-muted-foreground'
          }`}
        >
          <span className="min-w-0 flex-1">{value || placeholder}</span>
          <Pencil className="mt-0.5 h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-60" />
        </button>
      )}
    </div>
  );
}

export default function TaskDetailPanel({
  taskId, onClose, onChanged,
}: {
  taskId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await pmApi.getTask(taskId);
      if (res.success && res.data) setTask(res.data);
      else setError(res.error || 'Could not load this item.');
    } catch {
      setError('Could not load this item.');
    } finally { setLoading(false); }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (body: Record<string, unknown>) => {
    const res = await pmApi.updateTask(taskId, body);
    if (!res.success) { setError(res.error || res.message || 'Could not save.'); return; }
    await load();
    onChanged();
  };

  const locked = task ? task.externalSource !== 'manual' : true;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-3 border-b border-border p-4">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Item detail</p>
          <p className="mt-1 text-sm font-medium leading-snug text-[#242424] dark:text-foreground">
            {task ? cleanName(task.name) : 'Loading…'}
          </p>
        </div>
        <Button variant="ghost" size="sm" className="h-7 w-7 shrink-0 p-0" onClick={onClose} aria-label="Close detail">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {loading && !task ? <LoadingBlock /> : null}
        {error ? (
          <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400">
            {error}
          </p>
        ) : null}

        {task ? (
          <div className="space-y-5">
            {/* Chips */}
            <div className="flex flex-wrap items-center gap-1.5">
              <RiskLevel level={task.risk.level} />
              {task.isMilestone ? <Badge variant="secondary" className="text-[10px]"><Target className="mr-1 h-3 w-3" />Milestone</Badge> : null}
              {task.criticalPath ? <Badge variant="outline" className="text-[10px]">Critical path</Badge> : null}
              {task.blocked ? <Badge variant="destructive" className="text-[10px]">Blocked</Badge> : null}
              <OriginBadge origin={task.externalSource === 'manual' ? 'human_entered' : 'source'} />
            </div>

            {/* Facts */}
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div><dt className="text-xs text-muted-foreground">Department</dt><dd className="mt-0.5">{task.workstream}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Owner</dt><dd className="mt-0.5">{task.owner || '—'}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Due</dt><dd className="mt-0.5 tabular-nums">{fmtDate(task.dueDate)}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Progress</dt><dd className="mt-0.5 tabular-nums">{task.percentComplete}%</dd></div>
              {task.phase ? (
                <div className="col-span-2"><dt className="text-xs text-muted-foreground">Phase</dt><dd className="mt-0.5">{cleanName(task.phase)}</dd></div>
              ) : null}
            </dl>

            {/* Status — editable only where we own the value */}
            <div>
              <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Status {locked ? <Lock className="h-3 w-3" /> : null}
              </p>
              {locked ? (
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{task.status}</Badge>
                  <span className="text-xs text-muted-foreground">managed by the plan import</span>
                </div>
              ) : (
                <Select value={task.status || 'Not Started'} onValueChange={(v) => patch({ status: v })}>
                  <SelectTrigger className="h-8 w-full text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>

            {task.blockerReason ? (
              <div className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-300">
                {cleanBlocker(task.blockerReason)}
              </div>
            ) : null}

            <Separator />

            {/* The two fields leadership owns on every item */}
            <div className="space-y-3">
              <InlineField
                label="Next action"
                value={task.nextAction}
                placeholder="What must happen next to advance this?"
                emphasizeEmpty
                onSave={(v) => patch({ next_action: v })}
              />
              <InlineField
                label="Advancement criterion"
                value={task.advancementCriterion}
                placeholder="How will we know it moved?"
                onSave={(v) => patch({ advancement_criterion: v })}
              />
            </div>

            <Separator />

            {/* Why this risk level */}
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Why {task.risk.level.toLowerCase()} risk
              </p>
              <ReasonList reasons={task.risk.reasons} />
            </div>

            {/* Dependencies both ways */}
            {task.dependsOn.length || task.blocks.length ? (
              <>
                <Separator />
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <GitBranch className="h-3 w-3" /> Dependencies
                  </p>
                  <div className="space-y-2 text-sm">
                    {task.dependsOn.map((d) => (
                      <div key={d.id} className="rounded-md border border-border px-2.5 py-2">
                        <p className="text-xs text-muted-foreground">Waiting on</p>
                        <p>{d.depends_on_name ? cleanName(d.depends_on_name) : (d.unresolved_label || '—')}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] font-normal">
                            {d.confidence === 'parsed' ? 'Inferred from the plan' : 'Confirmed'}
                          </Badge>
                          {d.depends_on_status ? (
                            <span className="text-xs text-muted-foreground">
                              {d.depends_on_status === 'done' ? 'resolved' : 'outstanding'}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    ))}
                    {task.blocks.map((b) => (
                      <div key={b.id} className="rounded-md border border-border px-2.5 py-2">
                        <p className="text-xs text-muted-foreground">Blocks</p>
                        <p>{cleanName(b.task_name)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}

            {/* Item history */}
            <Separator />
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <History className="h-3 w-3" /> History
              </p>
              {task.events.length ? (
                <div className="space-y-2">
                  {task.events.map((e) => (
                    <div key={e.id} className="flex gap-2 text-xs">
                      <span className="w-16 shrink-0 text-muted-foreground">{fmtWhen(e.changed_at)}</span>
                      <span className="min-w-0 flex-1 text-foreground/75">
                        {e.change_kind === 'created' ? 'added to the plan'
                          : <>{e.field.replace(/_/g, ' ')}{e.before_value ? <> from <b className="font-medium">{e.before_value}</b></> : null} to <b className="font-medium">{e.after_value || '—'}</b></>}
                        <span className="text-muted-foreground"> · {e.source === 'human' ? 'entered here' : 'plan import'}</span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No changes recorded since import.</p>
              )}
            </div>

            {task.notes ? (
              <>
                <Separator />
                <div>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Notes from the plan</p>
                  <p className="text-sm text-foreground/75">{task.notes}</p>
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {!loading && !task && !error ? <EmptyState title="Item not found" /> : null}
      </div>
    </div>
  );
}
